/**
 * Tracked changes from before/after lines: field title once (first ": "), then
 * answer body with word-level diff — only removed words struck, only added words highlighted.
 *
 * When originalLines + afterLines are provided, renders a contextual diff:
 * changed sections are visible and unchanged sections can be expanded.
 */
import { Box, Divider, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconArrowsVertical, IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { useCallback, useState } from 'react';
import type { CSSProperties } from 'react';
import type { AddendumLineChange } from '@/pages/appointments/addendums/types';
import {
  diffAnswerWords,
  splitFieldTitleFromLine,
} from '@/pages/appointments/addendums/wordDiff';

const TEAL = '#0d5c5e';
const HIGHLIGHT_BG = '#c8ecea';
const GUTTER_WIDTH = 36;

function displayTitle(title: string): string {
  return title.endsWith(':') ? title.slice(0, -1) : title;
}

const answerLineStyle: CSSProperties = {
  color: 'var(--mantine-color-gray-9)',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  lineHeight: 1.65,
};

const removedInline: CSSProperties = {
  textDecoration: 'line-through',
  textDecorationColor: TEAL,
  color: TEAL,
};

const addedInline: CSSProperties = {
  backgroundColor: HIGHLIGHT_BG,
  color: TEAL,
  padding: '1px 4px',
  borderRadius: 3,
  boxDecorationBreak: 'clone',
  WebkitBoxDecorationBreak: 'clone',
};

function QuestionNumber({ n }: { n: number }): JSX.Element {
  return (
    <Box
      style={{
        width: GUTTER_WIDTH,
        flexShrink: 0,
        paddingTop: 2,
        textAlign: 'right',
        paddingRight: 8,
      }}
    >
      <Text size="xs" c="gray.4" style={{ fontFamily: 'monospace', userSelect: 'none' }}>
        {n}
      </Text>
    </Box>
  );
}

function AnswerWithWordDiff({ oldAnswer, newAnswer }: { oldAnswer: string; newAnswer: string }): JSX.Element {
  const segments = diffAnswerWords(oldAnswer, newAnswer);
  return (
    <Text component="div" size="sm" style={answerLineStyle}>
      {segments.map((seg, idx) => {
        const gap = idx > 0 && !seg.tightAfterPrev ? ' ' : '';
        if (seg.kind === 'same') return <Box component="span" key={idx}>{gap}{seg.text}</Box>;
        if (seg.kind === 'removed') return <Box component="span" key={idx} style={removedInline}>{gap}{seg.text}</Box>;
        return <Box component="span" key={idx} style={addedInline}>{gap}{seg.text}</Box>;
      })}
    </Text>
  );
}

function renderFullLineWordDiff(
  a: string,
  b: string,
  lineNumber?: number,
  dense?: boolean,
): JSX.Element {
  const segs = diffAnswerWords(a, b);
  return (
    <Box mb={dense ? 0 : 'md'}>
      <Box style={{ display: 'flex', gap: 0, alignItems: 'flex-start' }}>
        {lineNumber != null && <QuestionNumber n={lineNumber} />}
        <Text component="div" size="sm" style={{ ...answerLineStyle, flex: 1, minWidth: 0 }}>
          {segs.map((seg, idx) => (
            <Box
              component="span"
              key={idx}
              style={
                seg.kind === 'removed' ? removedInline : seg.kind === 'added' ? addedInline : undefined
              }
            >
              {idx > 0 && !seg.tightAfterPrev ? ' ' : ''}
              {seg.text}
            </Box>
          ))}
        </Text>
      </Box>
    </Box>
  );
}

function TrackedLineChange({
  removed,
  added,
  lineNumber,
  dense,
}: AddendumLineChange & { lineNumber?: number; dense?: boolean }): JSX.Element | null {
  const mb = dense ? 0 : 'md';
  if (removed && added) {
    const t0 = splitFieldTitleFromLine(removed);
    const t1 = splitFieldTitleFromLine(added);
    const sameField = t0.title.length > 0 && t1.title.length > 0 && t0.title === t1.title;
    if (!sameField) return renderFullLineWordDiff(removed, added, lineNumber, dense);
    return (
      <Box mb={mb}>
        <Box style={{ display: 'flex', gap: 0, alignItems: 'flex-start' }}>
          {lineNumber != null && <QuestionNumber n={lineNumber} />}
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Text size="sm" fw={600} c="gray.9" mb={6} style={{ lineHeight: 1.45 }}>
              {displayTitle(t0.title)}
            </Text>
            <AnswerWithWordDiff oldAnswer={t0.answer} newAnswer={t1.answer} />
          </Box>
        </Box>
      </Box>
    );
  }

  if (removed && !added) {
    const { title, answer } = splitFieldTitleFromLine(removed);
    return (
      <Box mb={mb}>
        <Box style={{ display: 'flex', gap: 0, alignItems: 'flex-start' }}>
          {lineNumber != null && <QuestionNumber n={lineNumber} />}
          <Box style={{ flex: 1, minWidth: 0 }}>
            {title && <Text size="sm" fw={600} c="gray.9" mb={6}>{displayTitle(title)}</Text>}
            <Text size="sm" style={{ ...removedInline, whiteSpace: 'pre-wrap' }}>{answer || removed}</Text>
          </Box>
        </Box>
      </Box>
    );
  }

  if (added && !removed) {
    const { title, answer } = splitFieldTitleFromLine(added);
    return (
      <Box mb={mb}>
        <Box style={{ display: 'flex', gap: 0, alignItems: 'flex-start' }}>
          {lineNumber != null && <QuestionNumber n={lineNumber} />}
          <Box style={{ flex: 1, minWidth: 0 }}>
            {title && <Text size="sm" fw={600} c="gray.9" mb={6}>{displayTitle(title)}</Text>}
            <Text size="sm" style={{ ...addedInline, whiteSpace: 'pre-wrap' }}>{answer || added}</Text>
          </Box>
        </Box>
      </Box>
    );
  }

  return null;
}

type ContextPosition = 'above' | 'between' | 'below';

interface ChangedSection {
  kind: 'changed';
  id: string;
  change: AddendumLineChange;
  lineIndex: number;
}

interface ContextSection {
  kind: 'context';
  id: string;
  position: ContextPosition;
  lines: string[];
  startLineIndex: number;
}

type DiffSection = ChangedSection | ContextSection;

function buildContextualSections(originalLines: string[], afterLines: string[]): DiffSection[] {
  const max = Math.max(originalLines.length, afterLines.length);
  type LineDiff =
    | { kind: 'unchanged'; text: string }
    | { kind: 'changed'; before?: string; after?: string };
  const diffs: LineDiff[] = [];

  for (let i = 0; i < max; i++) {
    const before = originalLines[i];
    const after = afterLines[i];
    if (before !== undefined && after !== undefined && before === after) {
      diffs.push({ kind: 'unchanged', text: before });
    } else {
      diffs.push({
        kind: 'changed',
        before: before !== undefined && before !== after ? before : undefined,
        after: after !== undefined && after !== before ? after : undefined,
      });
    }
  }

  const sections: DiffSection[] = [];
  let sectionIdx = 0;
  let i = 0;
  while (i < diffs.length) {
    const diff = diffs[i];
    if (diff.kind === 'changed') {
      sections.push({
        kind: 'changed',
        id: `chg-${sectionIdx++}`,
        change: { removed: diff.before, added: diff.after },
        lineIndex: i,
      });
      i++;
    } else {
      const start = i;
      while (i < diffs.length && diffs[i].kind === 'unchanged') i++;
      const lines = diffs
        .slice(start, i)
        .map((line) => (line as { kind: 'unchanged'; text: string }).text);
      const position: ContextPosition =
        sections.length === 0 ? 'above' : i === diffs.length ? 'below' : 'between';
      sections.push({
        kind: 'context',
        id: `ctx-${sectionIdx++}`,
        position,
        lines,
        startLineIndex: start,
      });
    }
  }

  return sections;
}

function NoteContextLine({
  text,
  lineNumber,
  isFirst,
}: {
  text: string;
  lineNumber: number;
  isFirst?: boolean;
}): JSX.Element {
  const { title, answer } = splitFieldTitleFromLine(text);
  return (
    <Box
      pt={isFirst ? 0 : 6}
      pb={6}
      style={{ display: 'flex', gap: 0, alignItems: 'flex-start' }}
    >
      <QuestionNumber n={lineNumber} />
      <Box style={{ flex: 1, minWidth: 0 }}>
        {title ? (
          <>
            <Text size="sm" fw={600} c="gray.8" mb={2}>{displayTitle(title)}</Text>
            <Text size="sm" c="gray.8" style={{ whiteSpace: 'pre-wrap' }}>{answer}</Text>
          </>
        ) : (
          <Text size="sm" c="gray.8" style={{ whiteSpace: 'pre-wrap' }}>{text}</Text>
        )}
      </Box>
    </Box>
  );
}

function ContextExpander({
  section,
  isExpanded,
  onExpand,
}: {
  section: ContextSection;
  isExpanded: boolean;
  onExpand: () => void;
}): JSX.Element {
  const { position, lines, startLineIndex } = section;
  const count = lines.length;

  if (isExpanded) {
    return (
      <Stack gap={0}>
        {lines.map((line, idx) => (
          <NoteContextLine
            key={idx}
            text={line}
            lineNumber={startLineIndex + idx + 1}
            isFirst={idx === 0}
          />
        ))}
      </Stack>
    );
  }

  const HunkIcon =
    position === 'above'
      ? IconChevronUp
      : position === 'below'
        ? IconChevronDown
        : IconArrowsVertical;

  return (
    <UnstyledButton
      onClick={onExpand}
      style={{ width: '100%' }}
      aria-label={`Expand ${count} ${position} line${count !== 1 ? 's' : ''}`}
    >
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '3px 8px',
          backgroundColor: '#eef6f6',
          border: '1px solid #c8ecea',
          borderRadius: 'var(--mantine-radius-sm)',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <HunkIcon size={13} color={TEAL} style={{ flexShrink: 0 }} />
        <Text size="xs" style={{ color: TEAL, fontFamily: 'monospace' }}>
          {count} line{count !== 1 ? 's' : ''}
        </Text>
      </Box>
    </UnstyledButton>
  );
}

function ContextualDiffView({
  originalLines,
  afterLines,
}: {
  originalLines: string[];
  afterLines: string[];
}): JSX.Element {
  const sections = buildContextualSections(originalLines, afterLines);
  const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({});
  const expand = useCallback((id: string) => {
    setExpandedIds((prev) => ({ ...prev, [id]: true }));
  }, []);

  if (!sections.some((section) => section.kind === 'changed')) {
    return <Text size="sm" c="gray.5">No changes detected.</Text>;
  }

  return (
    <Stack gap={0}>
      {sections.map((section) => (
        <Box key={section.id} mb="xs">
          {section.kind === 'changed' ? (
            <TrackedLineChange
              {...section.change}
              lineNumber={section.lineIndex + 1}
              dense
            />
          ) : (
            <ContextExpander
              section={section}
              isExpanded={Boolean(expandedIds[section.id])}
              onExpand={() => expand(section.id)}
            />
          )}
        </Box>
      ))}
    </Stack>
  );
}

export interface AddendumTrackedChangesViewProps {
  lineChanges?: AddendumLineChange[];
  comments?: string;
  fallbackBody?: string;
  originalLines?: string[];
  afterLines?: string[];
}

export function AddendumTrackedChangesView({
  lineChanges,
  comments,
  fallbackBody,
  originalLines,
  afterLines,
}: AddendumTrackedChangesViewProps): JSX.Element {
  const trimmedComments = comments?.trim() ?? '';
  const useContextual = (originalLines?.length ?? 0) > 0 && (afterLines?.length ?? 0) > 0;
  const hasStructured = (lineChanges?.length ?? 0) > 0;
  const showFallback = !hasStructured && !useContextual && (fallbackBody?.trim() ?? '').length > 0;

  if (!useContextual && !hasStructured && !trimmedComments && !showFallback) {
    return <Text size="sm" c="gray.5">—</Text>;
  }

  return (
    <Stack gap={0}>
      {useContextual ? (
        <ContextualDiffView originalLines={originalLines!} afterLines={afterLines!} />
      ) : (
        hasStructured && lineChanges!.map((change, index) => (
          <TrackedLineChange key={index} {...change} />
        ))
      )}
      {trimmedComments && (
        <>
          {(useContextual || hasStructured) && (
            <Divider label="Comments" labelPosition="left" my="sm" />
          )}
          <Text size="sm" c="gray.8" style={{ whiteSpace: 'pre-wrap' }}>{trimmedComments}</Text>
        </>
      )}
      {showFallback && (
        <Text size="sm" c="gray.8" style={{ whiteSpace: 'pre-wrap' }}>{fallbackBody!.trim()}</Text>
      )}
    </Stack>
  );
}
